"use client"

import { useEffect } from "react"
import { usePathname } from "next/navigation"
import {
  clearPageFamily,
  pageFamilyFromPathname,
  registerPageFamily,
} from "@/lib/analytics/content-links"
import { runWhenAnalyticsReady } from "@/lib/analytics/analytics-ready"

/**
 * Stamps `page_family` as a PostHog super property for blog and guide routes,
 * and releases it when the route leaves the family.
 *
 * Two registration paths, because `posthog.register` is a silent no-op until
 * `posthog.init()` runs from PHProvider's mount effect, which is AFTER every
 * child's effects on the hydration commit:
 * - during render, which only succeeds on client-side navigations (the SDK is
 *   already loaded) and lands before the incoming page's mount-time events;
 * - from an effect that polls until the SDK is loaded, which is what makes the
 *   first page load work at all.
 *
 * Durable caveat: on a hard load, events captured before init (the initial
 * `$pageview`, anything fired during head parsing) carry no `page_family`.
 */
export function PageFamilyTracker() {
  const pathname = usePathname()
  const family = pageFamilyFromPathname(pathname)

  if (typeof window !== "undefined") {
    registerPageFamily(family)
  }

  useEffect(() => {
    const cancel = runWhenAnalyticsReady(() => {
      registerPageFamily(family)
    })
    return () => {
      cancel()
      clearPageFamily(family)
    }
  }, [family])

  return null
}
